/* istanbul ignore file */
/**
 * Created by rathompson on 11/8/21
 * File: AssignRolesModalOld
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useMemo, useState, useContext } from 'react'
import {
  Button,
  Modal, ModalBody,
  ModalFooter,
  ModalHeader,
  Title,
  noop, Bold,
} from '@panwds/react-ui'
import {
  LABEL_ASSIGN_ROLES_TITLE,
  LABEL_ASSIGN_ROLES,
  LABEL_SELECTED_IDENTITIES,
  BUTTON_CANCEL,
  BUTTON_ASSIGN_ROLE,
} from '../../../../constants/LabelConstants'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import AssignRolesForm, { AssignRolesFormName } from '../../../common/AssignRolesForm/AssignRolesFormOld'
import Pill from '../../../../../common/components/pill/Pill'
import { useFormContext } from 'react-hook-form'
import { SubmitButton } from '@panwds/react-form'
import { prn } from '../../../../utils/prn'
import IAMService from '../../../../services/IAMService'
import GlobalContext from '../../../../../common/context/GlobalContext'
import useReadOnly from '../../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../../constants/IAMConstants'

const Footer = ({ isLoading, onCancel }) => {
  const { formState } = useFormContext()
  const { isValid } = formState

  return (
    <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
      <Button
        onClick={onCancel}
        appearance="secondary"
      >{BUTTON_CANCEL}</Button>
      <SubmitButton
        appearance='primary'
        disabled={isLoading || !isValid}
      >{BUTTON_ASSIGN_ROLE}</SubmitButton>
    </ModalFooter>
  )
}

/**
 *   Identity role assignment modal
 */
const AssignRolesModalOld = ({ isOpen, getData, closeModal: _closeModal, tenant, selected, roles }) => {

  const { accessPolicies } = IAMService
  const { treeReducer } = useContext(GlobalContext)

  /**
   *     ========= Local State ===========
   */
  const [identities, setIdentities] = useState(selected)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { selectedTreeNode = {} } = treeReducer
  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)

  /**
   *     ========= Callback ===========
   */
  const closeModal = useCallback((shouldFetch) => {
    _closeModal()
    if (shouldFetch && typeof shouldFetch === 'boolean') {
      getData()
    }
  }, [_closeModal, getData])

  const onRemove = useCallback((item) => {
    setIdentities(identities?.filter(identity => identity.identity_email !== item.identity_email))
  }, [identities])

  const onSubmit = useCallback(async (body) => {
    setLoading(true)
    try {
      for (const member of identities) {
        for (const [key, value] of Object.entries(body)) {
          const { protocol: type, app_id } = AssignRolesFormName.parse(key)
          if (type === 'apps' || value?.disabled || key.startsWith('undefined') || !value) {
            continue
          }
          if (type === 'role') {
            const { id: name } = value
            const appID = app_id === 'all' ? '' : app_id
            const resource = prn.format({
              protocol: 'prn',
              tsg_id: tenant?.id,
              app_id: appID,
              region_id: '',
              instance_id: '',
              resource_scope: ''
            })
            await accessPolicies.create(tenant?.id, {
              role: name,
              principal: member.identity_email,
              resource
            })
          }
        }
      }
      closeModal(true)
    }
    catch (error) {
      setError(error.message)
    }
    setLoading(false)
  }, [accessPolicies, closeModal, identities, tenant?.id])

  const onCancel = useCallback(() => {
    closeModal()
  }, [closeModal])

  const pills = useMemo(() => {
    return identities?.map((item, index) => (
      <Pill
        key={index}
        appearance='tertiary'
        removable={identities?.length > 1 || !readOnly}
        onDelete={onRemove?.bind(this, item)}
      >{item?.display_name?.trim() || item?.identity_email}</Pill>
    ))
  }, [identities, onRemove, readOnly])

  return (
    <Modal
      style={{ zIndex: 1052, top: '50%', '--tw-translate-y': '-50%', height: '600px', width: '800px', overflow: 'hidden' }}
      onClose={closeModal}
      isOpen={isOpen}
    >
      <ModalHeader title={StringTemplate(LABEL_ASSIGN_ROLES_TITLE, [tenant?.display_name])}/>
      <ModalBody addClassName='tw-overflow-auto'>
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        <div className="tw-w-full">
          <Title level={1} size={'xs'} addClassName='tw-flex tw-truncate'>{LABEL_SELECTED_IDENTITIES.toUpperCase()}</Title>
          <div className='tw-m-2 tw-flex tw-flex-wrap tw-gap-1 tw-overflow-y-scroll tw-max-h-40'>
            {pills}
          </div>
          <div className='tw-mt-4'>
            <Title level={1} size={'xs'} addClassName='tw-flex tw-truncate'>{LABEL_ASSIGN_ROLES.toUpperCase()}</Title>
            <AssignRolesForm {...{ isLoading, onSubmit, roles, tenant }}>
              <Footer {...{ isLoading, onCancel }} />
            </AssignRolesForm>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

AssignRolesModalOld.propTypes = {}

AssignRolesModalOld.defaultProps = {
  isOpen: false,
  closeModal: noop,
  tenant: {},
  selected: []
}

export default AssignRolesModalOld
